<template>
  <div>
    <PremiumModalForm
      :value="value"
      @input="$emit('input', $event)"
      title="Profile Settings"
      submitText="Save Changes"
      :loading="loading"
      @submit="updateProfile"
      maxWidth="500"
    >
      <v-form @submit.prevent="updateProfile">
        <!-- Profile Picture -->
        <div class="profile-pic-area">
          <input type="file" ref="profile_pic" accept="image/*" @change="onFileSelected" hidden>

          <div class="profile-avatar-wrap">
            <PremiumAvatar
              :image="previewUrl || (user.profile_url ? $resolveUploadUrl('super_admins/profile_pics/' + user.profile_url) : null)"
              :name="user.name"
              :size="90"
            />
            <button type="button" class="profile-edit-btn" @click="$refs.profile_pic.click()">
              <v-icon size="14" color="#fff">mdi-camera</v-icon>
            </button>
          </div>
          <div class="profile-pic-hint">Click the camera icon to change photo</div>
        </div>

        <!-- Form Fields -->
        <PremiumInput
          label="Full Name"
          v-model="user.name"
          name="name"
          placeholder="Enter your full name"
          icon="mdi-account-outline"
          v-validate="'required'"
          :error="errors.first('name') || serverSideErrors.name"
        />
        <PremiumInput
          label="Email Address"
          v-model="user.email"
          name="email"
          placeholder="you@example.com"
          type="email"
          icon="mdi-email-outline"
          disabled
        />
        <div class="ism-mobile-field">
          <label class="premium-label">Mobile Number</label>
          <div class="ism-mobile-row" :class="{ 'has-mobile-error': errors.has('Mobile No') }">
            <div class="ism-mobile-code">
              <div class="ism-country-selector-wrap">
                <country-code-select
                  v-model="user.countryIso"
                  @onSelect="onCountrySelect"
                />
              </div>
            </div>
            <div class="ism-mobile-number">
              <PremiumInput
                v-model="user.mobile_no"
                name="Mobile No"
                placeholder="Enter mobile number"
                v-validate="'numeric'"
                :error="errors.first('Mobile No') || serverSideErrors.mobile_no"
              />
            </div>
          </div>
        </div>
      </v-form>
    </PremiumModalForm>

    <!-- Image Cropper -->
    <ImageCropper
      v-model="cropperOpen"
      :image-src="rawImageSrc"
      title="Crop Profile Picture"
      shape="circle"
      :output-size="400"
      @cropped="onCropped"
    />
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import PremiumModalForm from '../common/PremiumModalForm.vue';
import PremiumInput from '../common/PremiumInput.vue';
import PremiumAvatar from '../common/PremiumAvatar.vue';
import ImageCropper from '../common/ImageCropper.vue';

export default {
  name: 'ProfileModal',
  components: { PremiumModalForm, PremiumInput, PremiumAvatar, ImageCropper },
  props: { value: { type: Boolean, default: false } },

  data() {
    return {
      loading:      false,
      user:         { name: '', email: '', mobile_no: '', profile_url: null, countryIso: 'in', country_code: '+91' },
      serverSideErrors: {},
      profile_pic:  null,
      previewUrl:   null,
      cropperOpen:  false,
      rawImageSrc:  '',
    };
  },

  methods: {
    ...mapMutations('alert', ['setAlert']),

    // Step 1: user picks a file → open cropper
    onFileSelected(e) {
      const file = e.target.files[0];
      if (!file) return;
      // Reset input so same file can be re-selected
      e.target.value = '';
      const reader = new FileReader();
      reader.onload = ev => {
        this.rawImageSrc = ev.target.result;
        this.cropperOpen = true;
      };
      reader.readAsDataURL(file);
    },

    // Step 2: cropper emits cropped file + preview
    onCropped({ file, previewUrl }) {
      this.profile_pic = file;
      this.previewUrl  = previewUrl;
    },

    onCountrySelect(e) {
      this.user.country_code = '+' + e.dialCode;
      this.user.countryIso = e.iso2;
    },

    updateProfile() {
      this.$validator.validateAll().then(result => {
        if (!result) return;
        this.loading = true;
        const formData = new FormData();
        formData.append('name', this.user.name);
        formData.append('email', this.user.email);
        formData.append('mobileNumber', this.user.mobile_no);
        formData.append('country_code', this.user.country_code);
        formData.append('countryIso', this.user.countryIso);
        if (this.profile_pic) formData.append('profile_pic', this.profile_pic);

        this.$axios.post('/superadmin/updateProfile', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        }).then(response => {
          this.loading = false;
          this.serverSideErrors = {};
          const data = response.data;
          if (data.status) {
            this.user = data.user;
            this.profile_pic = null;
            this.previewUrl  = null;
            data.user.role = 'superadmin';
            localStorage.setItem('user', JSON.stringify(data.user));
            if (data.user.profile_url) this.$emit('changeProfilePic');
            this.setAlert({ showAlert: true, message: data.message, variant: 'green accent-4', timeout: 3000 });
            this.$emit('input', false);
          } else {
            if (data.errors) {
              data.errors.forEach(err => { this.$set(this.serverSideErrors, err.field, err.message); });
              this.setAlert({ showAlert: true, message: 'Please correct the errors.', variant: 'red accent-3', timeout: 4000 });
            } else {
              this.setAlert({ showAlert: true, message: data.message || 'Something went wrong', variant: 'red accent-3', timeout: 3000 });
            }
          }
        }).catch(() => { this.loading = false; });
      });
    },
  },

  mounted() {
    const stored = localStorage.getItem('user');
    if (stored) this.user = JSON.parse(stored);

    this.$validator.localize('en', {
      custom: {
        'Mobile No': {
          numeric: 'Please enter a valid mobile number',
        },
        name: { required: 'Full name is required' }
      }
    });
  },
};
</script>

<style scoped>
.profile-pic-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
}
.profile-avatar-wrap {
  position: relative;
  width: 90px;
  height: 90px;
}
.profile-edit-btn {
  position: absolute;
  bottom: 0; right: 0;
  width: 30px; height: 30px;
  border-radius: 50%;
  background: #1B2340;
  border: 2px solid var(--card-bg, #fff);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.2s;
}
.profile-edit-btn:hover { background: #F26522; transform: scale(1.08); }
.profile-pic-hint {
  font-size: 11.5px;
  color: var(--text-secondary, #64748b);
  font-family: "Inter", sans-serif;
}

/* Mobile Row Alignment */
.ism-mobile-field { margin-bottom: 20px; text-align: left; }
.ism-mobile-row { display: flex; gap: 12px; align-items: flex-start; }
.ism-mobile-code { width: 80px; }
.ism-mobile-number { flex: 1; }

/* Sync borders on error */
.has-mobile-error .ism-country-selector-wrap {
  border-color: #ef4444 !important;
}
.has-mobile-error .ism-mobile-number >>> fieldset {
  border-color: #ef4444 !important;
}
.ism-mobile-number >>> .v-text-field__details {
  margin-left: -92px !important;
  width: calc(100% + 92px) !important;
  padding-left: 0 !important;
  padding-top: 6px !important;
  display: block !important;
}
.ism-mobile-number >>> .v-messages__message {
  text-align: left !important;
}

.premium-label {
  display: block; font-size: 13px; font-weight: 600;
  color: var(--text-secondary); margin-bottom: 8px;
  font-family: "Inter", sans-serif;
  white-space: nowrap;
}

.ism-country-selector-wrap {
  height: 40px; border: 1px solid var(--border-color2);
  border-radius: 8px; background: var(--bg-primary) !important;
  display: flex; align-items: center; 
  transition: all 0.2s;
  position: relative;
  overflow: visible;
}
.ism-country-selector-wrap:hover {
  border-color: var(--accent) !important;
}

.ism-country-selector-wrap >>> .dropdown:hover {
  background-color: transparent !important;
}

.ism-country-selector-wrap >>> .country-code-select,
.ism-country-selector-wrap >>> .vue-country-select,
.ism-country-selector-wrap >>> .selected-flag,
.ism-country-selector-wrap >>> .dropdown-button,
.ism-country-selector-wrap >>> .dropdown-toggle {
  border: none !important; 
  width: 100% !important; 
  height: 100% !important;
  background: transparent !important; 
  background-color: transparent !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 14px !important; 
  color: var(--text-primary) !important;
  box-shadow: none !important;
}

.ism-country-selector-wrap >>> .dropdown-list,
.ism-country-selector-wrap >>> ul {
  position: absolute !important;
  top: calc(100% + 4px);
  left: 0 !important;
  bottom: auto;
  background-color: var(--card-bg) !important;
  border: 1px solid var(--border-color2) !important;
  color: var(--text-primary) !important;
  z-index: 99999 !important;
  box-shadow: var(--shadow-lg) !important;
  border-radius: 8px !important;
  min-width: 250px !important;
  max-height: 250px !important;
  overflow-y: auto !important;
  padding: 6px 0 !important;
}

.ism-country-selector-wrap >>> li,
.ism-country-selector-wrap >>> .dropdown-item {
  padding: 8px 14px !important;
  background: transparent !important;
  color: var(--text-primary) !important;
  transition: all 0.2s;
}

.ism-country-selector-wrap >>> li:hover,
.ism-country-selector-wrap >>> .dropdown-item:hover {
  background-color: var(--bg-primary) !important;
  color: var(--accent) !important;
}
</style>
